import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface VenuesPaginationProps {
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
}

export function VenuesPagination({ page, limit, totalCount, onPageChange }: VenuesPaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div data-testid="venues-pagination" className="flex items-center justify-between gap-3">
      <p
        data-testid="venues-pagination-summary"
        className="font-sans text-sm font-normal text-muted-foreground"
      >
        {t("venuesDirectory.pagination.summary", { from, to, total: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          data-testid="venues-pagination-prev"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label={t("venuesDirectory.pagination.previous")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-sans text-sm font-medium tabular-nums text-foreground">
          {t("venuesDirectory.pagination.pageOf", { page, total: totalPages })}
        </span>
        <Button
          data-testid="venues-pagination-next"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-label={t("venuesDirectory.pagination.next")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
